/**
 * Learn tab — plain-language micro-lessons per product family, now with
 * embedded training videos.
 *
 * Each lesson card fetches training videos from the NAVI corpus by
 * `product_type` (same endpoint NAVI uses inside the journey), and lets the
 * team upload/remove videos inline. This is the single source of truth for
 * training video content.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { WalletShell, useWalletActive, NAVY, GOLD } from "@/components/wallet/WalletShell";
import { GraduationCap, ChevronRight, ChevronLeft, CheckCircle2, ShieldAlert, PlayCircle, Plus, X, Loader2, Trash2 } from "lucide-react";
import { emitSignal } from "@/lib/wallet-signal";
import VideoEmbed, { formatDuration } from "@/components/VideoEmbed";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const LESSONS = [
  {
    id: "life_insurance",
    title: "Life Insurance",
    brief: "A monthly payment (premium) that pays your family a lump sum if you pass away while the policy is active.",
    key_facts: [
      "The premium is calculated on your age, health and cover amount — it usually rises as you age.",
      "There is a waiting period (often 12–24 months) during which some causes of death are not paid out.",
      "'Exclusions' are situations that will NOT be paid — always read them before signing.",
      "You have a 31-day cooling-off period to cancel without penalty.",
    ],
    red_flags: [
      "The advisor pressures you to sign today.",
      "Cover amount is far higher than you need — larger cover means larger commission.",
    ],
  },
  {
    id: "retirement_annuity",
    title: "Retirement Annuity",
    brief: "A tax-advantaged savings product that you contribute to over many years, drawing an income from age 55 onwards.",
    key_facts: [
      "Contributions reduce your taxable income (up to 27.5% of gross remuneration, capped at R350 000/year).",
      "You can only access the money from age 55 — earlier withdrawal is heavily taxed.",
      "Fees compound. A 1% higher fee over 30 years can reduce your final pot by 20–30%.",
      "At retirement, you can take up to one-third as a lump sum; two-thirds must buy a pension.",
    ],
    red_flags: [
      "Fees quoted only as 'Total Investment Charge' without breakdown into advice, admin and platform.",
      "Product with 5+ year early-termination penalties.",
    ],
  },
  {
    id: "home_loan",
    title: "Home Loan (Bond)",
    brief: "Money the bank lends you to buy property, repayable over 20–30 years, with the property as security.",
    key_facts: [
      "Your interest rate is prime rate + a margin. The margin depends on your credit profile.",
      "Extra payments made into your bond save a LOT of interest — R500/month extra can cut years off.",
      "You will pay bond registration fees, transfer duty and attorney fees on top of the deposit.",
      "If you fall behind, the bank can repossess and sell the property.",
    ],
    red_flags: [
      "Advisor pushes you toward a longer term without explaining the total interest cost.",
      "'100% bond' with no deposit — you'll pay significantly more over the term.",
    ],
  },
  {
    id: "credit_card",
    title: "Credit Card",
    brief: "A revolving credit facility from a bank — you can spend up to a limit and repay monthly.",
    key_facts: [
      "Pay the full balance every month to avoid interest completely.",
      "Interest rates on unpaid balances are among the highest (typically 15–22% per year).",
      "'Minimum payment' keeps you in debt for years — always pay more than the minimum.",
      "Store cards look like credit cards but often have even higher interest.",
    ],
    red_flags: [
      "Being offered a large limit you didn't request.",
      "Instant approval without checking your affordability — often illegal under the National Credit Act.",
    ],
  },
  {
    id: "funeral_cover",
    title: "Funeral Cover",
    brief: "A small monthly premium that pays out a fixed amount to cover funeral costs when a family member passes away.",
    key_facts: [
      "You can cover a wider family than life insurance (extended family, in-laws).",
      "Payout is quick — usually within 48 hours of a valid claim.",
      "There is always a waiting period (typically 6–12 months) for natural-cause deaths.",
      "Fixed payout — it does not increase with inflation unless the policy specifically says so.",
    ],
    red_flags: [
      "Being sold multiple overlapping funeral policies for the same person.",
      "Provider is not registered with the FSCA (check on the FSCA website).",
    ],
  },
];

export default function WalletLearning() {
  const [selected, setSelected] = useState(null);
  const [videosByProduct, setVideosByProduct] = useState({}); // { product_type: [item, ...] }
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [uploadFor, setUploadFor] = useState(null); // { id, title } — opens modal
  const active = useWalletActive();

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/corpus/training-videos`)
      .then(r => {
        const map = {};
        (r.data || []).forEach(v => {
          const key = v.product_type || "_universal";
          (map[key] = map[key] || []).push(v);
        });
        setVideosByProduct(map);
      })
      .catch(() => setVideosByProduct({}))
      .finally(() => setLoading(false));
  }, [reloadKey]);

  const universalVideos = videosByProduct._universal || [];

  return (
    <WalletShell activeTab={active}>
      <div className="max-w-3xl mx-auto px-4 lg:px-8 py-6 space-y-4" data-testid="wallet-learning">
        <header>
          <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Learning</div>
          <h1 className="font-display text-2xl font-semibold" style={{ color: NAVY }}>Understand before you decide</h1>
          <p className="text-sm text-slate-600 mt-1">Short, plain-language lessons on the products banks most often push at you.</p>
        </header>

        {selected ? (
          <LessonDetail
            lesson={selected}
            videos={videosByProduct[selected.id] || []}
            onBack={() => setSelected(null)}
            onAddVideo={() => setUploadFor(selected)}
            onRemoveVideo={async (id) => {
              if (!window.confirm("Remove this training video?")) return;
              await axios.delete(`${API}/corpus/${id}`);
              setReloadKey(k => k + 1);
            }}
          />
        ) : (
          <>
            <div className="rounded-2xl bg-white border border-slate-100 shadow-sm divide-y divide-slate-100" data-testid="lesson-list">
              {LESSONS.map(l => {
                const vids = videosByProduct[l.id] || [];
                return (
                  <button
                    key={l.id}
                    onClick={() => { setSelected(l); emitSignal("lesson.opened.v1", { product_type: l.id, title: l.title, video_count: vids.length }); }}
                    data-testid={`lesson-${l.id}`}
                    className="w-full text-left p-4 flex items-start gap-3 hover:bg-slate-50 transition"
                  >
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${GOLD}22` }}>
                      <GraduationCap className="w-4 h-4" style={{ color: NAVY }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-semibold" style={{ color: NAVY }}>{l.title}</div>
                        {vids.length > 0 && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid={`video-badge-${l.id}`}>
                            <PlayCircle className="w-3 h-3" /> {vids.length} video{vids.length === 1 ? "" : "s"}
                          </span>
                        )}
                      </div>
                      <div className="text-[12px] text-slate-600 mt-0.5 leading-relaxed">{l.brief}</div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300 mt-1 flex-shrink-0" />
                  </button>
                );
              })}
            </div>

            {loading && (
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading training videos…
              </div>
            )}

            {/* Universal / product-agnostic videos */}
            {universalVideos.length > 0 && (
              <section className="rounded-2xl bg-white border border-slate-100 shadow-sm p-5" data-testid="universal-videos">
                <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: NAVY }}>General training</div>
                <div className="mt-3 space-y-4">
                  {universalVideos.map(v => (
                    <div key={v.id}>
                      <VideoEmbed url={v.video_url} title={v.title} duration_seconds={v.duration_seconds} />
                      <div className="mt-2 text-sm font-semibold" style={{ color: NAVY }}>{v.title}</div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>

      {uploadFor && (
        <UploadModal
          lesson={uploadFor}
          onClose={() => setUploadFor(null)}
          onSaved={() => { setUploadFor(null); setReloadKey(k => k + 1); }}
        />
      )}
    </WalletShell>
  );
}

function LessonDetail({ lesson, videos, onBack, onAddVideo, onRemoveVideo }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-5" data-testid="lesson-detail">
      <button onClick={onBack} className="text-xs inline-flex items-center gap-1 mb-3" style={{ color: NAVY }} data-testid="lesson-back">
        <ChevronLeft className="w-3.5 h-3.5" /> Back to lessons
      </button>
      <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Learning</div>
      <h2 className="font-display text-xl font-semibold mt-1" style={{ color: NAVY }}>{lesson.title}</h2>
      <p className="text-[15px] text-slate-800 mt-2 leading-relaxed">{lesson.brief}</p>

      {/* ═══ VIDEO LIBRARY — NAVI reads from here in every journey of this product_type ═══ */}
      <div className="mt-6 pt-5 border-t border-slate-100" data-testid={`lesson-videos-${lesson.id}`}>
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: NAVY }}>Training videos</div>
          <button
            onClick={onAddVideo}
            data-testid={`add-video-${lesson.id}`}
            className="text-[11px] font-medium inline-flex items-center gap-1 rounded-full px-3 py-1.5 transition"
            style={{ background: GOLD, color: NAVY }}
          >
            <Plus className="w-3 h-3" /> Add video
          </button>
        </div>
        <p className="text-[11px] text-slate-500 mt-1">
          NAVI auto-injects these into any journey where the pushed product matches <code className="bg-slate-100 rounded px-1 text-[10px]">{lesson.id}</code>.
        </p>

        {videos.length === 0 ? (
          <div className="mt-4 rounded-xl border-2 border-dashed border-slate-200 p-6 text-center" data-testid={`empty-videos-${lesson.id}`}>
            <PlayCircle className="w-8 h-8 mx-auto text-slate-300" />
            <div className="mt-2 text-sm text-slate-500">No videos yet for this product.</div>
            <button
              onClick={onAddVideo}
              className="mt-3 text-xs font-medium hover:underline"
              style={{ color: NAVY }}
            >
              Add the first one →
            </button>
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            {videos.map(v => (
              <div key={v.id} data-testid={`video-item-${v.id}`}>
                <VideoEmbed url={v.video_url} title={v.title} duration_seconds={v.duration_seconds} />
                <div className="mt-2 flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold" style={{ color: NAVY }}>{v.title}</div>
                    {v.notes && <div className="text-[11px] text-slate-500 mt-0.5">{v.notes}</div>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {v.duration_seconds > 0 && (
                      <span className="text-[10px] font-mono text-slate-500 tabular-nums rounded bg-slate-100 px-1.5 py-0.5">{formatDuration(v.duration_seconds)}</span>
                    )}
                    <button
                      onClick={() => onRemoveVideo(v.id)}
                      data-testid={`remove-video-${v.id}`}
                      className="text-slate-400 hover:text-rose-600 transition"
                      aria-label="Remove video"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Existing text lesson content */}
      <div className="mt-6 pt-5 border-t border-slate-100">
        <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: NAVY }}>Things to know</div>
        <ul className="mt-2 space-y-2">
          {lesson.key_facts.map((f, i) => (
            <li key={i} className="flex items-start gap-2 text-[13px] text-slate-700">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: GOLD }} />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-5 rounded-xl p-3" style={{ background: "#FEF2F2", border: "1px solid #FCA5A5" }}>
        <div className="text-[10px] uppercase tracking-[0.22em] text-rose-700 font-semibold">Watch out for</div>
        <ul className="mt-2 space-y-2">
          {lesson.red_flags.map((r, i) => (
            <li key={i} className="flex items-start gap-2 text-[13px] text-rose-900">
              <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5 text-rose-600" />
              <span>{r}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function UploadModal({ lesson, onClose, onSaved }) {
  const [form, setForm] = useState({ title: "", video_url: "", duration_seconds: "", notes: "" });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.video_url.trim()) { setError("Video URL required."); return; }
    const fd = new FormData();
    fd.append("kind", "training_video");
    fd.append("product_type", lesson.id);
    fd.append("video_url", form.video_url.trim());
    if (form.title) fd.append("title", form.title);
    if (form.duration_seconds) fd.append("duration_seconds", String(form.duration_seconds));
    if (form.notes) fd.append("notes", form.notes);
    setUploading(true);
    try {
      await axios.post(`${API}/corpus`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="upload-modal">
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="px-5 py-4 flex items-center justify-between border-b border-slate-100">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Add training video</div>
            <div className="text-sm font-semibold" style={{ color: NAVY }}>{lesson.title}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-800 transition" data-testid="upload-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-3">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Video URL</span>
            <input value={form.video_url} onChange={e => setForm({ ...form, video_url: e.target.value })} data-testid="modal-video-url" placeholder="https://www.youtube.com/watch?v=…" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" required />
            <div className="text-[10px] text-slate-400 mt-1">YouTube, Vimeo, Loom, or direct MP4 URL.</div>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Title (optional)</span>
            <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="modal-title" placeholder={`e.g. Understanding ${lesson.title} in 3 minutes`} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Duration (seconds, optional)</span>
            <input type="number" min={0} value={form.duration_seconds} onChange={e => setForm({ ...form, duration_seconds: e.target.value })} data-testid="modal-duration" placeholder="180" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Notes (context for consumers)</span>
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="modal-notes" rows={2} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          </label>

          {error && <div className="rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-800 p-2">{error}</div>}

          <button type="submit" disabled={uploading} data-testid="modal-submit" className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition disabled:opacity-40" style={{ background: NAVY, color: "white" }}>
            {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><PlayCircle className="w-4 h-4" /> Save video</>}
          </button>
        </form>
      </div>
    </div>
  );
}
